import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Loader from 'components/loader/Loader';
import React, { useEffect, useState } from 'react';
import {
  followPodcasts,
  getPreferencesAllPodcasts,
  IPodcastImage,
  IPreferencePodcast,
  IResponseData,
} from 'store';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import theme from 'theme';
import { compareArrayObjects } from 'utils/utils';

import SavePreferencesBtn from '../SavePreferencesBtn';

interface IProps {
  searchQuery: string;
}

function Podcasts({ searchQuery }: IProps) {
  const dispatch = useAppDispatch();
  const { allPodcastsLoading, allPodcasts, followPodcastsLoading } = useAppSelector(
    s => s.followingTimeline,
  );
  const [podcasts, setPodcasts] = useState<IPreferencePodcast[]>(
    allPodcasts.length > 0 ? allPodcasts : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('midsm'));

  const fetchAllPodcasts = async () => {
    const response = (await dispatch(getPreferencesAllPodcasts())) as IResponseData;
    if (!response.error) {
      setPodcasts(response.data);
    }
  };

  const handleCheck = (index: number) => {
    const updatedState: IPreferencePodcast[] = podcasts.map((p: IPreferencePodcast, i: number) => {
      if (index === i) {
        return { ...p, isFollowed: !p.isFollowed };
      }
      return p;
    });
    setPodcasts(updatedState);
  };

  const handleDisable = () => {
    return compareArrayObjects(podcasts, allPodcasts, 'isFollowed');
  };

  const saveSelectedPodcasts = async () => {
    const podcastIds = podcasts?.reduce((a: string[], c: IPreferencePodcast) => {
      if (c.isFollowed) {
        a.push(c.documentId);
      }
      return a;
    }, []);
    await dispatch(followPodcasts(podcastIds, podcasts));
  };

  const filteredData = podcasts.filter(
    (podcast: IPreferencePodcast, index: number, arr: IPreferencePodcast[]) => {
      if (searchQuery.length >= 3) {
        return podcast.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return arr;
    },
  );

  useEffect(() => {
    if (allPodcasts.length === 0) {
      setIsLoading(true);
      fetchAllPodcasts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allPodcastsLoading !== isLoading) {
      setIsLoading(allPodcastsLoading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPodcastsLoading]);

  return (
    <>
      <Stack
        className="custom-pref-scrollbar"
        height={isMobile ? 'calc(100vh - 275px)' : '100%'}
        paddingBottom={theme.spacing(1)}
        paddingRight={theme.spacing(2)}
        spacing={theme.spacing(1.5)}
        sx={{
          overflowY: 'auto',
        }}
      >
        <>
          {isLoading ? (
            <Loader customPaddingThemeSpacing={2} size={30} />
          ) : filteredData && filteredData.length > 0 ? (
            <>
              {filteredData.map((podcast: IPreferencePodcast, index: number) => {
                const podcastProviderLogo: IPodcastImage[] = podcast?.externalShowImages
                  ? JSON.parse(podcast.externalShowImages)
                  : [];
                return (
                  <Stack
                    key={podcast.documentId + index.toString()}
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Stack alignItems="center" direction="row">
                      <Avatar
                        alt={podcast.name ?? 'provider-logo'}
                        src={podcastProviderLogo?.[0]?.url || podcast.name}
                        sx={{
                          width: theme.spacing(3),
                          height: theme.spacing(2.75),
                          background: theme.palette.secondary.main,
                          marginRight: theme.spacing(2),
                        }}
                        variant="square"
                      />
                      <Typography className="text-ellipsis text-clamp-2">{podcast.name}</Typography>
                    </Stack>
                    <IconButton
                      aria-label="remove"
                      color="primary"
                      size="medium"
                      sx={{ padding: theme.spacing(0) }}
                      onClick={() => handleCheck(podcasts.indexOf(podcast))}
                    >
                      {podcast.isFollowed ? (
                        <CheckCircleIcon fontSize="inherit" />
                      ) : (
                        <ControlPointIcon fontSize="inherit" />
                      )}
                    </IconButton>
                  </Stack>
                );
              })}
            </>
          ) : (
            !allPodcastsLoading &&
            filteredData.length === 0 && (
              <Stack alignItems="center" flexGrow={1} height="100%" justifyContent="center">
                <Typography align="center" component="div" variant="title2">
                  No podcasts found
                </Typography>
              </Stack>
            )
          )}
        </>
      </Stack>
      <SavePreferencesBtn
        disableFn={handleDisable}
        loading={followPodcastsLoading}
        onSave={saveSelectedPodcasts}
      />
    </>
  );
}

export default Podcasts;
